import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, X, Play, CheckCircle, DollarSign } from "lucide-react";
import { apiBaseUrl } from '../../../../../../../services/config';
import { useTheme } from "context/ThemeContext";
const API = apiBaseUrl;
const ax  = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || localStorage.getItem("platform_token") || ""}` } });

const FREQ  = ["Monthly","Quarterly","Half-yearly","Yearly","One-time"];
const CALC  = ["Fixed","% of CTC","% of Basic"];
const APPLY = ["All","Department","Designation"];

// ── Period helper ─────────────────────────────────────────────────
const suggestPeriod = (freq) => {
  const now = new Date();
  const y   = now.getFullYear();
  const m   = String(now.getMonth() + 1).padStart(2,"0");
  const q   = Math.ceil((now.getMonth()+1)/3);
  if (freq === "Monthly")     return `${y}-${m}`;
  if (freq === "Quarterly")   return `${y}-Q${q}`;
  if (freq === "Half-yearly") return now.getMonth() < 6 ? `${y}-H1` : `${y}-H2`;
  if (freq === "Yearly")      return `${y}-${y+1}`;
  return `${y}-${m}`;
};

// ── Stat card ─────────────────────────────────────────────────────
const StatCard = ({ label, value, color, icon }) => {
  const { T } = useTheme();
  return (
    <div style={{ padding:"16px 20px", background:T.card, border:`1px solid ${T.border}`,
      borderRadius:12, flex:1, minWidth:160 }}>
      <div style={{ fontSize:11, color:T.muted, fontWeight:700, textTransform:"uppercase",
        letterSpacing:"0.08em", marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:900, color }}>{value}</div>
    </div>
  );
};

export default function BonusManagement() {
  const { T } = useTheme();
  const STATUS_COLOR = { Draft:T.amber, Approved:T.cyan, Paid:T.green };
  const inp = (x={}) => ({ width:"100%", padding:"8px 12px", border:`1px solid ${T.border}`,
    borderRadius:7, background:T.inp_bg, color:T.text, fontFamily:"Outfit,sans-serif",
    fontSize:13, outline:"none", boxSizing:"border-box", ...x });
  const lbl = { fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase",
    letterSpacing:"0.07em", display:"block", marginBottom:4 };

  const [tab,        setTab]        = useState("policies");
  const [policies,   setPolicies]   = useState([]);
  const [disbursals, setDisbursals] = useState([]);
  const [summary,    setSummary]    = useState({});
  const [depts,      setDepts]      = useState([]);
  const [desigs,     setDesigs]     = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [toast,      setToast]      = useState("");

  // Modals
  const [policyModal, setPolicyModal] = useState(false);
  const [editPolicy,  setEditPolicy]  = useState(null);
  const [disburseModal,setDisburseModal]=useState(null); // policy object
  const [period,      setPeriod]      = useState("");

  // Filters
  const [filterPolicy,setFilterPolicy]=useState("");
  const [filterStatus,setFilterStatus]=useState("");

  const flash = (msg) => { setToast(msg); setTimeout(()=>setToast(""),3500); };
  const fmt   = (n) => n != null ? `₹${Number(n).toLocaleString("en-IN",{maximumFractionDigits:0})}` : "—";

  const EMPTY_POLICY = {
    name:"", description:"", frequency:"Yearly", calculation_type:"Fixed",
    value:0, is_taxable:true, applicable_to:"All",
    department_id:null, designation_id:null, is_active:true,
  };
  const [pForm, setPForm] = useState(EMPTY_POLICY);
  const setP = (k,v) => setPForm(f=>({...f,[k]:v}));

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [polRes, disRes, sumRes, dRes, dgRes] = await Promise.allSettled([
        axios.get(`${API}/bonuses/policies`, ax()),
        axios.get(`${API}/bonuses/disbursals`, ax()),
        axios.get(`${API}/bonuses/summary`,   ax()),
        axios.get(`${API}/departments/`,       ax()),
        axios.get(`${API}/designations/`,      ax()),
      ]);
      if (polRes.status==="fulfilled") setPolicies(polRes.value.data||[]);
      if (disRes.status==="fulfilled") setDisbursals(disRes.value.data||[]);
      if (sumRes.status==="fulfilled") setSummary(sumRes.value.data||{});
      if (dRes.status==="fulfilled")   setDepts(dRes.value.data||[]);
      if (dgRes.status==="fulfilled")  setDesigs(dgRes.value.data||[]);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  // ── Policy CRUD ───────────────────────────────────────────────
  const openCreate = () => { setPForm(EMPTY_POLICY); setEditPolicy(null); setPolicyModal(true); };
  const openEdit   = (p)  => { setPForm({...p}); setEditPolicy(p); setPolicyModal(true); };

  const savePolicy = async () => {
    if (!pForm.name.trim()) return flash("❌ Policy name is required");
    try {
      if (editPolicy) {
        await axios.put(`${API}/bonuses/policies/${editPolicy.id}`, pForm, ax());
        flash("✅ Policy updated");
      } else {
        await axios.post(`${API}/bonuses/policies`, pForm, ax());
        flash("✅ Policy created");
      }
      setPolicyModal(false); loadAll();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Save failed")); }
  };

  const deletePolicy = async (id) => {
    if (!window.confirm("Delete this bonus policy?")) return;
    try {
      await axios.delete(`${API}/bonuses/policies/${id}`, ax());
      flash("✅ Deleted"); loadAll();
    } catch(e) { flash("❌ Delete failed"); }
  };

  // ── Disbursal ────────────────────────────────────────────────
  const runDisbursal = async () => {
    if (!period.trim()) return flash("❌ Enter a period");
    try {
      const res = await axios.post(
        `${API}/bonuses/disburse?policy_id=${disburseModal.id}&period=${period}`, {}, ax()
      );
      flash(`✅ ${res.data.message}`);
      setDisburseModal(null); setTab("disbursals"); loadAll();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Failed")); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/bonuses/disbursals/${id}`, { status }, ax());
      flash(`✅ Marked as ${status}`); loadAll();
    } catch(e) { flash("❌ Update failed"); }
  };

  const updateAmount = async (id, amount) => {
    try {
      await axios.put(`${API}/bonuses/disbursals/${id}`, { amount: parseFloat(amount) }, ax());
      loadAll();
    } catch(e) {}
  };

  const bulkApprove = async (policyId, per) => {
    try {
      const r = await axios.post(
        `${API}/bonuses/disbursals/bulk-approve?policy_id=${policyId}&period=${per}`, {}, ax());
      flash(`✅ Approved ${r.data.approved} records`); loadAll();
    } catch(e) { flash("❌ Bulk approve failed"); }
  };

  const bulkPaid = async (policyId, per) => {
    try {
      const r = await axios.post(
        `${API}/bonuses/disbursals/bulk-paid?policy_id=${policyId}&period=${per}`, {}, ax());
      flash(`✅ Marked ${r.data.paid} as Paid`); loadAll();
    } catch(e) { flash("❌ Bulk paid failed"); }
  };

  // ── Filtered disbursals ───────────────────────────────────────
  const filteredDis = disbursals.filter(d =>
    (!filterPolicy || d.policy_id === Number(filterPolicy)) &&
    (!filterStatus || d.status === filterStatus)
  );

  // Group disbursals by policy+period for bulk actions
  const groups = [...new Set(filteredDis.map(d => `${d.policy_id}||${d.period}`))].map(key => {
    const [pid, per] = key.split("||");
    const rows = filteredDis.filter(d => d.policy_id===Number(pid) && d.period===per);
    return { policyId:Number(pid), period:per, policyName:rows[0]?.policy_name, rows };
  });

  return (
    <div style={{ fontFamily:"Outfit,sans-serif", color:T.text }}>

      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:11, color:T.cyan, fontWeight:700,
          letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>PAYROLL</div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px", color:T.text }}>
              Bonus Management
            </h2>
            <p style={{ fontSize:13.5, color:T.muted, margin:0 }}>
              Configure policies, run disbursals, approve & mark paid
            </p>
          </div>
          {tab==="policies" && (
            <button onClick={openCreate}
              style={{ display:"flex", alignItems:"center", gap:7, padding:"10px 20px",
                borderRadius:10, border:"none",
                background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                color:"#fff", fontFamily:"Outfit,sans-serif",
                fontSize:13, fontWeight:800, cursor:"pointer" }}>
              <Plus size={14}/> New Bonus Policy
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display:"flex", gap:12, marginBottom:24, flexWrap:"wrap" }}>
        <StatCard label="Active Policies" value={summary.active_policies||0}   color={T.cyan}/>
        <StatCard label="Draft Amount"    value={fmt(summary.draft_amount)}     color={T.amber}/>
        <StatCard label="Approved Amount" value={fmt(summary.approved_amount)}  color={T.cyan}/>
        <StatCard label="Paid Amount"     value={fmt(summary.paid_amount)}      color={T.green}/>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:4, borderBottom:`1px solid ${T.border}`, marginBottom:20 }}>
        {["policies","disbursals"].map(t=>(
          <button key={t} onClick={()=>setTab(t)}
            style={{ padding:"9px 20px", borderRadius:"8px 8px 0 0",
              border:`1px solid ${tab===t?T.border:"transparent"}`,
              borderBottom:"none", background:tab===t?T.card:T.card2,
              color:tab===t?T.cyan:T.muted, cursor:"pointer",
              fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700,
              textTransform:"capitalize", marginBottom:tab===t?-1:0 }}>
            {t === "policies" ? "📋 Bonus Policies" : "💰 Disbursals"}
          </button>
        ))}
      </div>

      {/* ── POLICIES TAB ── */}
      {tab==="policies" && (
        <div style={{ background:T.card, border:`1px solid ${T.border}`,
          borderRadius:14, overflow:"hidden" }}>
          {loading ? (
            <div style={{ padding:40, textAlign:"center", color:T.muted }}>Loading…</div>
          ) : policies.length===0 ? (
            <div style={{ padding:60, textAlign:"center" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🎁</div>
              <div style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:6 }}>
                No bonus policies yet
              </div>
              <div style={{ fontSize:13, color:T.muted, marginBottom:20 }}>
                Create your first policy — Diwali bonus, performance bonus, etc.
              </div>
              <button onClick={openCreate}
                style={{ padding:"10px 24px", borderRadius:9, border:"none",
                  background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                  color:"#fff", fontFamily:"Outfit,sans-serif",
                  fontSize:13, fontWeight:800, cursor:"pointer" }}>
                + New Bonus Policy
              </button>
            </div>
          ) : (
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr style={{ background:T.card2 }}>
                    {["Policy Name","Frequency","Calculation","Value","Applies To","Status","Actions"].map(h=>(
                      <th key={h} style={{ padding:"12px 16px", textAlign:"left",
                        fontSize:11, color:T.dim, fontWeight:700,
                        textTransform:"uppercase", letterSpacing:"0.06em",
                        borderBottom:`1px solid ${T.border}`, whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {policies.map(p=>(
                    <tr key={p.id} style={{ borderBottom:`1px solid ${T.border}` }}
                      onMouseEnter={e=>e.currentTarget.style.background=T.sidebar_hover}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{ padding:"12px 16px" }}>
                        <div style={{ fontWeight:700, color:T.text }}>{p.name}</div>
                        {p.description && (
                          <div style={{ fontSize:11.5, color:T.dim, marginTop:2 }}>{p.description}</div>
                        )}
                      </td>
                      <td style={{ padding:"12px 16px" }}>
                        <span style={{ fontSize:11, padding:"3px 10px", borderRadius:100,
                          background:`${T.cyan}18`, color:T.cyan, fontWeight:700 }}>
                          {p.frequency}
                        </span>
                      </td>
                      <td style={{ padding:"12px 16px", fontSize:13, color:T.muted }}>
                        {p.calculation_type}
                      </td>
                      <td style={{ padding:"12px 16px", fontSize:13, fontWeight:700, color:T.text }}>
                        {p.calculation_type==="Fixed"
                          ? fmt(p.value)
                          : `${p.value}%`}
                      </td>
                      <td style={{ padding:"12px 16px", fontSize:13, color:T.muted }}>
                        {p.applicable_to === "All" ? "All Employees" :
                         p.applicable_to === "Department" ? (p.department_name||"Dept") :
                         (p.designation_title||"Desig")}
                      </td>
                      <td style={{ padding:"12px 16px" }}>
                        <span style={{ fontSize:11, padding:"3px 10px", borderRadius:100,
                          background:p.is_active?`${T.green}18`:`${T.dim}18`,
                          color:p.is_active?T.green:T.dim, fontWeight:700 }}>
                          {p.is_active?"Active":"Inactive"}
                        </span>
                      </td>
                      <td style={{ padding:"12px 16px" }}>
                        <div style={{ display:"flex", gap:6 }}>
                          <button onClick={()=>{ setDisburseModal(p); setPeriod(suggestPeriod(p.frequency)); }}
                            title="Run Disbursal"
                            style={{ width:30, height:30, borderRadius:7,
                              border:`1px solid ${T.green}4D`, background:"none",
                              color:T.green, cursor:"pointer",
                              display:"flex", alignItems:"center", justifyContent:"center" }}>
                            <Play size={12}/>
                          </button>
                          <button onClick={()=>openEdit(p)} title="Edit"
                            style={{ width:30, height:30, borderRadius:7,
                              border:`1px solid ${T.cyan}33`, background:"none",
                              color:T.cyan, cursor:"pointer",
                              display:"flex", alignItems:"center", justifyContent:"center" }}>
                            <Edit2 size={12}/>
                          </button>
                          <button onClick={()=>deletePolicy(p.id)} title="Delete"
                            style={{ width:30, height:30, borderRadius:7,
                              border:`1px solid ${T.red}33`, background:"none",
                              color:T.red, cursor:"pointer",
                              display:"flex", alignItems:"center", justifyContent:"center" }}>
                            <Trash2 size={12}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── DISBURSALS TAB ── */}
      {tab==="disbursals" && (
        <div>
          {/* Filters */}
          <div style={{ display:"flex", gap:10, marginBottom:16 }}>
            <select value={filterPolicy} onChange={e=>setFilterPolicy(e.target.value)}
              style={{ ...inp(), width:"auto", cursor:"pointer" }}>
              <option value="">All Policies</option>
              {policies.map(p=><option key={p.id} value={p.id} style={{background:T.opt_bg}}>{p.name}</option>)}
            </select>
            <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
              style={{ ...inp(), width:"auto", cursor:"pointer" }}>
              <option value="">All Status</option>
              {["Draft","Approved","Paid"].map(s=><option key={s} style={{background:T.opt_bg}}>{s}</option>)}
            </select>
          </div>

          {groups.length===0 ? (
            <div style={{ padding:60, textAlign:"center", background:T.card,
              border:`1px solid ${T.border}`, borderRadius:14 }}>
              <div style={{ fontSize:40, marginBottom:12 }}>💰</div>
              <div style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:6 }}>
                No disbursals yet
              </div>
              <div style={{ fontSize:13, color:T.muted, marginBottom:20 }}>
                Go to Policies and click ▶ to run a disbursal
              </div>
              <button onClick={()=>setTab("policies")}
                style={{ padding:"9px 20px", borderRadius:8,
                  border:`1px solid ${T.border}`, background:"none",
                  color:T.cyan, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
                View Policies
              </button>
            </div>
          ) : groups.map(g=>(
            <div key={`${g.policyId}-${g.period}`} style={{
              background:T.card, border:`1px solid ${T.border}`,
              borderRadius:14, marginBottom:16, overflow:"hidden" }}>
              {/* Group header */}
              <div style={{ padding:"12px 18px", background:T.card2,
                display:"flex", justifyContent:"space-between", alignItems:"center",
                borderBottom:`1px solid ${T.border}` }}>
                <div>
                  <span style={{ fontSize:14, fontWeight:700, color:T.text }}>
                    {g.policyName}
                  </span>
                  <span style={{ marginLeft:10, fontSize:12, color:T.muted }}>
                    Period: <strong style={{color:T.cyan}}>{g.period}</strong>
                  </span>
                  <span style={{ marginLeft:10, fontSize:12, color:T.muted }}>
                    {g.rows.length} employees ·{" "}
                    Total: <strong style={{color:T.green}}>
                      {fmt(g.rows.reduce((s,r)=>s+r.amount,0))}
                    </strong>
                  </span>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  {g.rows.some(r=>r.status==="Draft") && (
                    <button onClick={()=>bulkApprove(g.policyId, g.period)}
                      style={{ padding:"6px 14px", borderRadius:7,
                        border:`1px solid ${T.cyan}4D`, background:`${T.cyan}1A`,
                        color:T.cyan, cursor:"pointer",
                        fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:700 }}>
                      ✓ Approve All
                    </button>
                  )}
                  {g.rows.some(r=>r.status==="Approved") && (
                    <button onClick={()=>bulkPaid(g.policyId, g.period)}
                      style={{ padding:"6px 14px", borderRadius:7,
                        border:`1px solid ${T.green}4D`, background:`${T.green}1A`,
                        color:T.green, cursor:"pointer",
                        fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:700 }}>
                      💰 Mark Paid
                    </button>
                  )}
                </div>
              </div>
              {/* Employee rows */}
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr>
                    {["Employee","Code","Department","Calculated","Final Amount","Status","Action"].map(h=>(
                      <th key={h} style={{ padding:"10px 16px", textAlign:"left",
                        fontSize:11, color:T.dim, fontWeight:700,
                        textTransform:"uppercase", letterSpacing:"0.06em",
                        borderBottom:`1px solid ${T.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map(d=>(
                    <tr key={d.id} style={{ borderBottom:`1px solid ${T.border}` }}
                      onMouseEnter={e=>e.currentTarget.style.background=T.sidebar_hover}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{ padding:"10px 16px", fontWeight:600, color:T.text }}>
                        {d.employee_name}
                      </td>
                      <td style={{ padding:"10px 16px", fontSize:12, color:T.muted }}>
                        {d.employee_code}
                      </td>
                      <td style={{ padding:"10px 16px", fontSize:12, color:T.muted }}>
                        {d.department||"—"}
                      </td>
                      <td style={{ padding:"10px 16px", fontSize:13, color:T.dim }}>
                        {fmt(d.calculated_amount)}
                      </td>
                      <td style={{ padding:"10px 16px" }}>
                        {d.status==="Draft" ? (
                          <input
                            type="number"
                            defaultValue={d.amount}
                            onBlur={e=>updateAmount(d.id, e.target.value)}
                            style={{ ...inp(), width:120, padding:"5px 8px" }}/>
                        ) : (
                          <span style={{ fontSize:13, fontWeight:700, color:T.green }}>
                            {fmt(d.amount)}
                          </span>
                        )}
                      </td>
                      <td style={{ padding:"10px 16px" }}>
                        <span style={{ fontSize:11, padding:"3px 10px", borderRadius:100,
                          background:`${STATUS_COLOR[d.status]||T.muted}18`,
                          color:STATUS_COLOR[d.status]||T.muted, fontWeight:700 }}>
                          {d.status}
                        </span>
                      </td>
                      <td style={{ padding:"10px 16px" }}>
                        {d.status==="Draft" && (
                          <button onClick={()=>updateStatus(d.id,"Approved")}
                            style={{ fontSize:11, padding:"4px 10px", borderRadius:7,
                              border:`1px solid ${T.cyan}4D`, background:"none",
                              color:T.cyan, cursor:"pointer", fontFamily:"Outfit,sans-serif" }}>
                            Approve
                          </button>
                        )}
                        {d.status==="Approved" && (
                          <button onClick={()=>updateStatus(d.id,"Paid")}
                            style={{ fontSize:11, padding:"4px 10px", borderRadius:7,
                              border:`1px solid ${T.green}4D`, background:"none",
                              color:T.green, cursor:"pointer", fontFamily:"Outfit,sans-serif" }}>
                            Mark Paid
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* ── Policy Modal ── */}
      {policyModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:1000,
          display:"flex", alignItems:"center", justifyContent:"center",
          padding:16, backdropFilter:"blur(4px)" }}
          onClick={e=>{if(e.target===e.currentTarget)setPolicyModal(false);}}>
          <div style={{ background:T.card, border:`1px solid ${T.border_strong}`,
            borderRadius:16, width:"100%", maxWidth:580,
            maxHeight:"90vh", display:"flex", flexDirection:"column",
            boxShadow:T.shadow }}>
            <div style={{ padding:"18px 24px 14px", borderBottom:`1px solid ${T.border}`,
              background:`linear-gradient(135deg,${T.cyan}0F,transparent)`,
              display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
              <div>
                <div style={{ fontSize:11, color:T.cyan, fontWeight:700,
                  letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:2 }}>
                  {editPolicy ? "EDIT POLICY" : "NEW BONUS POLICY"}
                </div>
                <h3 style={{ margin:0, fontSize:15, fontWeight:800, color:T.text }}>
                  {editPolicy ? editPolicy.name : "Configure Bonus Policy"}
                </h3>
              </div>
              <button onClick={()=>setPolicyModal(false)}
                style={{ border:"none", background:T.toggle_bg,
                  borderRadius:"50%", width:30, height:30, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center", color:T.muted }}>
                <X size={14}/>
              </button>
            </div>
            <div style={{ padding:24, overflowY:"auto", flex:1 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                {/* Name */}
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={lbl}>Policy Name *</label>
                  <input value={pForm.name} onChange={e=>setP("name",e.target.value)}
                    placeholder="e.g. Diwali Bonus, Q1 Performance" style={inp()}/>
                </div>
                {/* Description */}
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={lbl}>Description</label>
                  <input value={pForm.description||""} onChange={e=>setP("description",e.target.value)}
                    placeholder="Optional note" style={inp()}/>
                </div>
                {/* Frequency */}
                <div>
                  <label style={lbl}>Frequency</label>
                  <select value={pForm.frequency} onChange={e=>setP("frequency",e.target.value)}
                    style={inp({cursor:"pointer"})}>
                    {FREQ.map(f=><option key={f} style={{background:T.opt_bg}}>{f}</option>)}
                  </select>
                </div>
                {/* Calculation type */}
                <div>
                  <label style={lbl}>Calculation Type</label>
                  <select value={pForm.calculation_type} onChange={e=>setP("calculation_type",e.target.value)}
                    style={inp({cursor:"pointer"})}>
                    {CALC.map(c=><option key={c} style={{background:T.opt_bg}}>{c}</option>)}
                  </select>
                </div>
                {/* Value */}
                <div>
                  <label style={lbl}>
                    {pForm.calculation_type==="Fixed" ? "Amount (₹)" : "Percentage (%)"}
                  </label>
                  <input type="number" value={pForm.value}
                    onChange={e=>setP("value",parseFloat(e.target.value)||0)}
                    placeholder={pForm.calculation_type==="Fixed"?"e.g. 5000":"e.g. 10"}
                    style={inp()}/>
                </div>
                {/* Taxable */}
                <div style={{ display:"flex", alignItems:"center", gap:10, paddingTop:20 }}>
                  <input type="checkbox" id="taxable" checked={pForm.is_taxable}
                    onChange={e=>setP("is_taxable",e.target.checked)}
                    style={{ width:16, height:16, cursor:"pointer" }}/>
                  <label htmlFor="taxable" style={{ fontSize:13, color:T.text, cursor:"pointer" }}>
                    Taxable
                  </label>
                </div>
                {/* Applicable to */}
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={lbl}>Applicable To</label>
                  <select value={pForm.applicable_to} onChange={e=>setP("applicable_to",e.target.value)}
                    style={inp({cursor:"pointer"})}>
                    {APPLY.map(a=><option key={a} style={{background:T.opt_bg}}>{a}</option>)}
                  </select>
                </div>
                {pForm.applicable_to==="Department" && (
                  <div style={{ gridColumn:"1/-1" }}>
                    <label style={lbl}>Department</label>
                    <select value={pForm.department_id||""}
                      onChange={e=>setP("department_id",Number(e.target.value)||null)}
                      style={inp({cursor:"pointer"})}>
                      <option value="" style={{background:T.opt_bg}}>— Select —</option>
                      {depts.map(d=><option key={d.id} value={d.id} style={{background:T.opt_bg}}>{d.name}</option>)}
                    </select>
                  </div>
                )}
                {pForm.applicable_to==="Designation" && (
                  <div style={{ gridColumn:"1/-1" }}>
                    <label style={lbl}>Designation</label>
                    <select value={pForm.designation_id||""}
                      onChange={e=>setP("designation_id",Number(e.target.value)||null)}
                      style={inp({cursor:"pointer"})}>
                      <option value="" style={{background:T.opt_bg}}>— Select —</option>
                      {desigs.map(d=><option key={d.id} value={d.id} style={{background:T.opt_bg}}>{d.title}</option>)}
                    </select>
                  </div>
                )}
                {/* Active */}
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <input type="checkbox" id="active" checked={pForm.is_active}
                    onChange={e=>setP("is_active",e.target.checked)}
                    style={{ width:16, height:16, cursor:"pointer" }}/>
                  <label htmlFor="active" style={{ fontSize:13, color:T.text, cursor:"pointer" }}>
                    Active
                  </label>
                </div>
              </div>
            </div>
            <div style={{ padding:"14px 24px", borderTop:`1px solid ${T.border}`,
              display:"flex", gap:10, justifyContent:"flex-end",
              background:T.card2, flexShrink:0 }}>
              <button onClick={()=>setPolicyModal(false)}
                style={{ padding:"8px 18px", borderRadius:8,
                  border:`1px solid ${T.border}`, background:"none",
                  color:T.muted, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
                Cancel
              </button>
              <button onClick={savePolicy}
                style={{ padding:"8px 24px", borderRadius:8, border:"none",
                  background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                  color:"#fff", cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:800 }}>
                {editPolicy ? "Update Policy" : "Create Policy"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Run Disbursal Modal ── */}
      {disburseModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:1000,
          display:"flex", alignItems:"center", justifyContent:"center",
          backdropFilter:"blur(4px)" }}
          onClick={e=>{if(e.target===e.currentTarget)setDisburseModal(null);}}>
          <div style={{ background:T.card, border:`1px solid ${T.border_strong}`,
            borderRadius:14, width:420, overflow:"hidden",
            boxShadow:T.shadow }}>
            <div style={{ padding:"18px 22px 14px",
              borderBottom:`1px solid ${T.border}`,
              background:`${T.green}0F`,
              display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:11, color:T.green, fontWeight:700,
                  letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:2 }}>
                  RUN DISBURSAL
                </div>
                <h3 style={{ margin:0, fontSize:15, fontWeight:800, color:T.text }}>
                  {disburseModal.name}
                </h3>
              </div>
              <button onClick={()=>setDisburseModal(null)}
                style={{ border:"none", background:T.toggle_bg,
                  borderRadius:"50%", width:30, height:30, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center", color:T.muted }}>
                <X size={14}/>
              </button>
            </div>
            <div style={{ padding:"20px 22px" }}>
              <div style={{ marginBottom:14, padding:"10px 14px",
                background:`${T.cyan}08`, border:`1px solid ${T.border}`, borderRadius:8,
                fontSize:12, color:T.muted, lineHeight:1.6 }}>
                <strong style={{color:T.text}}>Frequency:</strong> {disburseModal.frequency} ·{" "}
                <strong style={{color:T.text}}>Calculation:</strong>{" "}
                {disburseModal.calculation_type === "Fixed"
                  ? `₹${disburseModal.value}`
                  : `${disburseModal.value}% of ${disburseModal.calculation_type.split("% of ")[1]}`}
              </div>
              <label style={lbl}>Period *</label>
              <input value={period} onChange={e=>setPeriod(e.target.value)}
                placeholder={`e.g. ${suggestPeriod(disburseModal.frequency)}`}
                style={inp({ marginBottom:6 })}/>
              <div style={{ fontSize:11.5, color:T.dim, marginBottom:18 }}>
                Examples: <code style={{color:T.cyan}}>2025-04</code> (monthly) ·{" "}
                <code style={{color:T.cyan}}>2025-Q2</code> (quarterly) ·{" "}
                <code style={{color:T.cyan}}>2025-26</code> (yearly)
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                <button onClick={()=>setDisburseModal(null)}
                  style={{ padding:"8px 18px", borderRadius:8,
                    border:`1px solid ${T.border}`, background:"none",
                    color:T.muted, cursor:"pointer",
                    fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
                  Cancel
                </button>
                <button onClick={runDisbursal}
                  style={{ padding:"8px 22px", borderRadius:8, border:"none",
                    background:`linear-gradient(135deg,${T.green},${T.cyan})`,
                    color:"#fff", cursor:"pointer",
                    fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:800 }}>
                  Generate Records
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%",
          transform:"translateX(-50%)", padding:"10px 22px",
          borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
          background:T.card,
          border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.red}4D`}`,
          color:toast.startsWith("✅")?T.green:T.red,
          boxShadow:T.shadow, fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
}
